import type { FormValues, Schedule } from './types'
import type { ReportConfig } from '../../../domain/cr-report-type.enum'

import { v4 as uuid } from '@lukeed/uuid'
import dayjs from 'dayjs'

import { CRReportType, label as getLabel } from '../../../domain/cr-report-type.enum'
import { isEqual, pickBy } from '../../../lib/lodash'

import { weekDayNames, weekStartDay } from './constants'

export function humanReadableTime(date: Date): string {
  const d = dayjs(date)
  const today = dayjs()

  if (d.isSame(today, 'day')) {
    return `today at ${d.format('HH:mm')}`
  }

  if (d.isSame(today.add(1, 'day'), 'day')) {
    return `tomorrow at ${d.format('HH:mm')}`
  }

  return `${d.format('MM/DD/YYYY')} at ${d.format('HH:mm')}`
}

export function humanReadableSchedule(schedule: Schedule): string {
  const days = generateWeek()
  const weeks = Array.from({ length: 5 }, (_, i) => i + 1)

  const isEveryDay =
    Object.keys(schedule).length === days.length &&
    Object.values(schedule).every(w => (w?.length ?? 0) === weeks.length)

  if (isEveryDay) {
    return 'Every day'
  }

  const isNever = Object.keys(schedule).length === 0

  if (isNever) {
    return 'Never'
  }

  const result: string[] = []

  for (const day of days) {
    if (!day) {
      continue
    }

    const k = day.slice(0, 3).toUpperCase()
    const v = schedule[k] ?? []

    if (v.length === weeks.length) {
      result.push(`every ${day}`)
      continue
    }

    if (v.length === 0) {
      continue
    }

    const n = v.map(week => ordinal(week))
    result.push(`every ${humanJoin(n)} ${day}`)
  }

  const joined = humanJoin(result)
  return joined.slice(0, 1).toUpperCase() + joined.slice(1)
}
function sortMaybeObjects<T>(arr: T[]): T[] {
  if (arr.every(x => typeof x !== 'object')) {
    return [...arr].sort()
  }
  return [...arr]
    .map(x => JSON.stringify(x))
    .sort()
    .map(s => JSON.parse(s) as T)
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100

  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0] ?? '')
}

export function humanJoin(items: string[]): string {
  if (items.length === 0) {
    return ''
  }

  if (items.length === 1) {
    return items[0] || ''
  }

  if (items.length === 2) {
    return items.join(' and ')
  }

  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

export function pluralize(n: number, singular: string, plural?: string): string {
  return n === 1 ? singular : (plural ?? `${singular}s`)
}

export function isEmptySchedule(schedule?: Schedule): boolean {
  return (
    !schedule ||
    Object.keys(schedule).length === 0 ||
    Object.values(schedule).every(w => !(w?.length ?? 0))
  )
}

export function generateWeek(
  startDay: number = weekStartDay
): Array<(typeof weekDayNames)[number]> {
  const days = weekDayNames.filter(Boolean)
  return [...days.slice(startDay), ...days.slice(0, startDay)]
}

export function getCurrentWeekDateFromDay(day: (typeof weekDayNames)[number]): string {
  const now = new Date()
  const currentDay = now.getDay()
  const diff = weekDayNames.indexOf(day) - currentDay

  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff)
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short' }).format(date)
}

export function ensureRequiredReportConfigs(reports: ReportConfig[]): ReportConfig[] {
  const required: ReportConfig[] = [
    {
      id: uuid(),
      type: CRReportType.Detail,
      options: {
        includeDiagnosisDetails: false,
        includePHI: true
      }
    },
    {
      id: uuid(),
      type: CRReportType.Summary
    }
  ]

  return [
    ...reports,
    ...required.filter(requiredReportConfig => {
      if (requiredReportConfig.type === CRReportType.Detail) {
        return !reports.some(
          report =>
            report.type === requiredReportConfig.type &&
            report.options.includeDiagnosisDetails ===
              requiredReportConfig.options.includeDiagnosisDetails &&
            report.options.includePHI === requiredReportConfig.options.includePHI
        )
      }

      return !reports.some(report => report.type === requiredReportConfig.type)
    })
  ]
}

export function getIsFormDirty(
  initialFormValues: FormValues | undefined,
  currentFormValues: FormValues | undefined
): boolean {
  const { id: _, ...current } = currentFormValues ?? {}
  const { id: __, ...initial } = initialFormValues ?? {}

  function getFormValuesWithSortedArrays(formValues: typeof current): typeof current {
    return Object.keys(formValues).reduce<typeof current>((acc, key) => {
      const value = formValues[key as keyof typeof current]
      if (Array.isArray(value)) {
        return {
          ...acc,
          [key]: sortMaybeObjects(value)
        }
      } else if (key === 'schedule') {
        return {
          ...acc,
          [key]: pickBy(value, v => v !== undefined)
        }
      }
      return {
        ...acc,
        [key]: value
      }
    }, {})
  }

  const initialSorted = getFormValuesWithSortedArrays(initial)
  const currentSorted = getFormValuesWithSortedArrays(current)

  return !isEqual(initialSorted, currentSorted)
}

export function getReportConfigLabel(config: ReportConfig): string {
  return getLabel(config.type, config.type === CRReportType.Detail ? config.options : undefined)
}
